import type {
	MatrixQuarterlyReport,
	Revision as RevisionModel,
} from "epsm-api-types/models";
import { useEffect, useState } from "react";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import { Spinner } from "@/components/Spinner";
import { DEFAULT_STATE_VALUES } from "@/shared/constants/common";
import { UseQuarterlyReportData } from "../../Chart/services/UseQuarterlyReportData";
import { UseData } from "../lib/services/UseData";
import {
	AnalystsEstimatesChartTimeFrame,
	AnalystsEstimatesChartViewMode,
	MatrixReportField,
	RevisionDataSource,
	RevisionDataValueMode,
	RevisionDataViewMode,
	type RevisionState,
	RevisionsScoreChartDataSource,
} from "../types";
import {
	CompetitorsGrid,
	Estimates,
	EstimatesRevision,
	RevisionData,
	ScoreChart,
} from "./components";
import { EarningMatrix } from "./components/EarningMatrix";
import { todayData } from "./components/EstimatesChart/lib/helpers";

export const Revision = ({
	state,
	tileId,
	onStateChange,
}: {
	state: RevisionState;
	tileId: string;
	onStateChange?: OnStateChange;
}) => {
	const ticker = state?.ticker ?? DEFAULT_STATE_VALUES.ticker;
	const earningsMatrixViewMode =
		state?.earningsMatrixViewMode ?? MatrixReportField.EPS_BNRI;
	const revisionsScoreChartDataSource =
		state?.revisionsScoreChartDataSource ?? RevisionsScoreChartDataSource.ZACKS;
	const analystsEstimatesChartTimeFrame =
		state?.analystsEstimatesChartTimeFrame ??
		AnalystsEstimatesChartTimeFrame.QUARTERS;
	const analystsEstimatesChartViewMode =
		state?.analystsEstimatesChartViewMode ??
		AnalystsEstimatesChartViewMode.STD_DEVIATION;
	const revisionDataViewMode =
		state?.revisionDataViewMode ?? RevisionDataViewMode.EPS_RS;
	const revisionDataValueMode =
		state?.revisionDataValueMode ?? RevisionDataValueMode.CHANGES;
	const revisionDataSource =
		state?.revisionDataSource ?? RevisionDataSource.ZACKS;

	const { execute, isLoading } = UseData.create().useGetData();
	const { execute: executeQuarterly, isLoading: isLoadingQuarterly } =
		UseQuarterlyReportData.create().useGetQuarterlyReports();

	const [quarterlyReports, setQuarterlyReports] = useState<
		MatrixQuarterlyReport[]
	>([]);
	const [dataRevision, setDataRevision] = useState<RevisionModel[] | undefined>(
		undefined,
	);
	const [lastRevision, setLastRevision] = useState<RevisionModel | undefined>(
		undefined,
	);
	const [error, setError] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (!dataRevision?.length) return;

		const fetchData = async () => {
			const fromDate = dataRevision[0].date.getTime();
			const result = await executeQuarterly({
				params: {
					fields: [
						"eps",
						"sales",
						"profit_margin",
						"free_cash_flow",
						"eps_bnri",
						"price_change_since_ed",
						"em",
						"earning_date",
						"next_earning_date",
					],
					sort: [
						{
							name: "earning_date",
							order: -1,
						},
						{
							name: "quarter_end_date",
							order: 1,
						},
					],
					filter: [
						{
							name: "ticker",
							condition: "eq",
							value: ticker,
						},
						{
							name: "quarter_end_date",
							condition: "gte",
							value: fromDate,
						},
					],
				},
			});
			setQuarterlyReports(result?.data ?? []);
		};

		fetchData();
	}, [dataRevision, ticker, executeQuarterly]);

	useEffect(() => {
		const getData = async () => {
			try {
				const result = await execute({
					params: {
						sort: [{ name: "date", order: 1 }],
						fields: [
							"dp",
							"rs",
							"rss",
							"rs_chg",
							"rss_chg",
							"mt",
							"ex",
							"date",
						],
						filter: [{ name: "ticker", condition: "eq", value: ticker }],
					},
				});

				if (result?.data?.length) {
					setDataRevision(result.data);
					setLastRevision(result.data[result.data.length - 1]);
				} else {
					setDataRevision(undefined);
					setLastRevision(undefined);
				}
			} catch (err) {
				setLastRevision(undefined);
				setDataRevision(undefined);
				setError(typeof err === "string" ? err : "Failed to fetch data");
			}
		};

		if (ticker) {
			getData();
		}
	}, [ticker, execute]);

	return (
		<div className="flex flex-wrap gap-4 max-w-[190ch] @container">
			{isLoading || isLoadingQuarterly ? (
				<Spinner />
			) : error ? (
				<div className="p-4 text-center text-red-500">
					<p>{error}</p>
				</div>
			) : lastRevision ? (
				<>
					{Boolean(quarterlyReports.length) && (
						<EarningMatrix
							data={quarterlyReports}
							earningsMatrixViewMode={earningsMatrixViewMode}
							onStateChange={onStateChange}
						/>
					)}
					<EstimatesRevision data={lastRevision} />
					<ScoreChart
						ticker={ticker}
						data={dataRevision!}
						quarterlyReports={quarterlyReports}
						revisionsScoreChartDataSource={revisionsScoreChartDataSource}
						onStateChange={onStateChange}
					/>
					<CompetitorsGrid
						tileId={tileId}
						ticker={ticker}
						lastRevisionDate={lastRevision.date}
					/>
					{Boolean(todayData(dataRevision!)?.dp?.ns?.ee?.length) && (
						<Estimates
							data={dataRevision}
							analystsEstimatesChartTimeFrame={analystsEstimatesChartTimeFrame}
							analystsEstimatesChartViewMode={analystsEstimatesChartViewMode}
							onStateChange={onStateChange}
						/>
					)}
					{Boolean(lastRevision.mt || lastRevision.dp || lastRevision.ex) && (
						<RevisionData
							data={lastRevision}
							revisionDataViewMode={revisionDataViewMode}
							revisionDataValueMode={revisionDataValueMode}
							revisionDataSource={revisionDataSource}
							onStateChange={onStateChange}
						/>
					)}
				</>
			) : (
				<div className="p-4 text-center text-light-gray">
					{ticker ? (
						<p>No revision data available for this ticker.</p>
					) : (
						<p>No ticker is selected.</p>
					)}
				</div>
			)}
		</div>
	);
};
